import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OPS_PASSWORD_MAX_LENGTH, OPS_PASSWORD_MIN_LENGTH } from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, StatusLamp } from '../ui/index.ts';

/**
 * Unauthenticated consume of a password-invite token mailed to the
 * email stored on the ops account. Production never lets an admin
 * type the other person's password into the API; this page is the
 * self-set half of that split (router#2032). The stored email is
 * whatever the creator wrote — this is not a proof that a second
 * human received the token.
 */

export function SetPasswordPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (token.length === 0) {
      setError(t('ops_users.set_password_page.missing_token'));
      return;
    }
    if (password.length < OPS_PASSWORD_MIN_LENGTH || password.length > OPS_PASSWORD_MAX_LENGTH) {
      setError(
        t('ops_users.set_password.error.length', {
          min: String(OPS_PASSWORD_MIN_LENGTH),
          max: String(OPS_PASSWORD_MAX_LENGTH),
        }),
      );
      return;
    }
    if (password !== confirm) {
      setError(t('ops_users.set_password.error.mismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await opsApiRequest<{ ok: true }>(
        '/v1/auth/complete-password-invite',
        { method: 'POST', body: { token, password } },
      );
      setDone(true);
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-[400px] rounded-xl border border-line bg-card p-8 text-paper shadow-raised">
        <div className="mb-7 flex items-center gap-3">
          <BrandLogo className="h-10 w-10 shrink-0 text-paper" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-title text-paper">{t('ops_users.set_password_page.brand')}</span>
            <span className="font-mono text-micro text-paper-low">
              {t('ops_users.set_password_page.subtitle')}
            </span>
          </div>
          <span className="ml-auto inline-flex items-center gap-1" aria-hidden="true">
            <StatusLamp tone="ok" size="sm" />
            <StatusLamp tone="off" size="sm" />
            <StatusLamp tone="off" size="sm" />
          </span>
        </div>
        {done ? (
          <div className="flex flex-col gap-4" data-testid="ops-set-password-success">
            <p className="m-0 text-body text-paper-mid">{t('ops_users.set_password_page.success')}</p>
            <Button
              type="button"
              variant="primary"
              className="h-9 w-full"
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('ops_users.set_password_page.to_login')}
            </Button>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <FormField label={t('ops_users.set_password_page.password')} fieldId="invite-password">
              <Input
                id="invite-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                disabled={submitting}
                required
                data-testid="ops-set-password-value"
              />
            </FormField>
            <FormField label={t('ops_users.set_password_page.confirm')} fieldId="invite-password-confirm">
              <Input
                id="invite-password-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
                disabled={submitting}
                required
                data-testid="ops-set-password-confirm-value"
              />
            </FormField>
            {error !== null ? (
              <p
                className="m-0 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
                role="alert"
                data-testid="ops-set-password-error"
              >
                {error}
              </p>
            ) : null}
            <Button type="submit" variant="primary" disabled={submitting} data-testid="ops-set-password-submit">
              {submitting
                ? t('ops_users.set_password_page.submitting')
                : t('ops_users.set_password_page.submit')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
